import type { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import type { ZodSchema } from 'zod';

import { logoutUser } from './auth';
import { ApiError } from './error';
import type {
  ApiEndpointMethod,
  ApiErrorResponse,
  DefaultParamsType,
  RequestParamsBase,
  RequestParamsMakeRequest,
} from './types';
import { paramsToString } from './utility';

import { config } from '../config';
import { CookieManager, snackbar } from '../utility';

export class ApiClient {
  private baseUrl = '';

  constructor(baseUrl = config.baseURL) {
    this.baseUrl = baseUrl;
  }

  private async baseRequest({
    endpoint,
    init,
    method = 'GET',
    body = undefined,
    params = {},
    isAuth = true,
    onUploadProgress,
  }: RequestParamsBase): Promise<AxiosResponse> {
    let searchParams;
    if (typeof params === 'string' && params !== '') {
      searchParams = new URLSearchParams(params);
    } else if (!!params && typeof params === 'object') {
      searchParams = new URLSearchParams(paramsToString(params));
    }

    let nBody;
    if (method !== 'GET') {
      if (body instanceof FormData) {
        nBody = body;
      } else {
        nBody = JSON.stringify(body);
      }
    }

    return axios({
      ...init,
      baseURL: this.baseUrl,
      url: endpoint,
      params: searchParams,
      method,
      data: nBody,
      // withCredentials: isAuth,
      onUploadProgress: onUploadProgress
        ? (progressEvent) => {
            const rawProgress = progressEvent.total ? progressEvent.loaded / progressEvent.total : 0;
            const progress = Math.round(rawProgress * 100);
            onUploadProgress(progress, rawProgress);
          }
        : undefined,
      headers: {
        ...(init?.headers || {}),
        ...(isAuth ? { Authorization: `Bearer ${CookieManager.getAccessToken()}` } : {}),
      },
    });
  }

  private validateResponse(response: unknown, schema?: ZodSchema): void {
    if (!schema || !response) return;
    const validationResult = schema.safeParse(
      typeof response === 'object' && 'data' in response ? response.data : response
    );
    if (validationResult && !validationResult.success) {
      console.error(validationResult.error);
      // snackbar.error(validationResult.error.toString());
    }
  }

  private displayError(error: AxiosError<ApiErrorResponse>): void {
    const errorData = error.response?.data;
    let errorMsg = 'An unknown error occurred.';
    if (errorData) {
      if (errorData.error) {
        errorMsg = errorData.error as string;
      } else if (errorData.message) {
        errorMsg = errorData.message;
      } else if (typeof errorData === 'string') {
        errorMsg = errorData;
      }
      snackbar.error(errorMsg, {
        toastId: 'error-msg',
      });
    } else {
      console.error('An unknown error occurred.');
      snackbar.error('An unknown error occurred.', {
        toastId: 'unknown-error',
      });
    }
  }

  private parseApiError(errorResponse?: ApiErrorResponse): ApiError {
    if (typeof errorResponse === 'string') {
      return new ApiError(errorResponse, null);
    } else if (!errorResponse) {
      return new ApiError('An unknown error occurred.', null);
    }
    let errorMessage = 'An unknown error occurred.';

    if (errorResponse.error) {
      errorMessage = errorResponse.error as string;
    } else if (errorResponse.details) {
      const [key] = Object.keys(errorResponse.details);
      const [value] = errorResponse.details[key];
      errorMessage = value;
    } else if (errorResponse.message) {
      errorMessage = errorResponse.message;
    }

    return new ApiError(errorMessage, errorResponse);
  }

  private async makeRequest<T, P extends DefaultParamsType = DefaultParamsType>({
    responseSchema,
    endpoint,
    isAuth = true,
    ...params
  }: RequestParamsMakeRequest<P>): Promise<T> {
    try {
      // Base request call
      const res = await this.baseRequest({ endpoint, isAuth, ...params });

      if (res.status === 204) {
        // Empty response
        return res as T;
      }
      const response = res.data as T;
      this.validateResponse(response, responseSchema);
      return response;
    } catch (e: unknown) {
      const errorResponse = e as AxiosError<ApiErrorResponse>;

      if (errorResponse.name === 'AxiosError' && endpoint === '/auth/logout') {
        return {} as T;
      }

      if ((errorResponse?.response?.status ?? 0) < 500) {
        this.displayError(errorResponse);
      }
      if (errorResponse.response && errorResponse.response.status >= 400) {
        // Handle unauthorized response
        if (isAuth && errorResponse.response.status === 401) {
          await logoutUser();
        }
      }

      throw this.parseApiError(errorResponse.response?.data);
    }
  }

  async get<T, P extends DefaultParamsType = DefaultParamsType>({
    endpoint,
    params,
    init = {},
    ...rest
  }: Omit<RequestParamsMakeRequest<P>, 'body' | 'method'>): Promise<T> {
    return this.makeRequest<T>({
      endpoint,
      params,
      init,
      ...rest,
    });
  }

  async request<T, P>({
    endpoint,
    payload,
    init = {},
    method = 'POST',
    ...rest
  }: Omit<RequestParamsBase, 'body' | 'method'> & {
    payload?: P;
    method?: ApiEndpointMethod;
  }): Promise<T> {
    return this.makeRequest<T>({
      endpoint,
      method,
      body: payload,
      init: {
        ...init,
        headers: {
          ...(init.headers || { 'Content-Type': 'application/json' }),
        },
      },
      ...rest,
    });
  }
}
